import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import machinesData from '../data/machines.json';
import Footer from './Footer';
import MachineInquiryModal from './MachineInquiryModal';

interface Machine {
  Type: string;
  Name: string;
  Brand: string;
  Year: string;
  Hours: string;
  Description: string;
  Price: string;
  ShippingEstimate: string;
  ImageURL: string;
  Status: string;
}

const PricingPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [machines] = useState<Machine[]>(machinesData);
  const [selectedMachine, setSelectedMachine] = useState<Machine | null>(null);
  const [isInquiryModalOpen, setIsInquiryModalOpen] = useState(false);

  const handleNavToImports = () => {
    navigate({ pathname: '/imports', search: `?lng=${i18n.language}` });
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const renderMachines = (type: string) => {
    const filtered = machines.filter(m => (m.Type || '').trim().toLowerCase() === type.toLowerCase());

    let sectionKey: 'pricingPage.sectionNew' | 'pricingPage.sectionUsed' | 'pricingPage.sectionSourced';
    switch (type.toLowerCase()) {
      case 'new':
        sectionKey = 'pricingPage.sectionNew';
        break;
      case 'used':
        sectionKey = 'pricingPage.sectionUsed';
        break;
      case 'sourced':
        sectionKey = 'pricingPage.sectionSourced';
        break;
      default:
        sectionKey = 'pricingPage.sectionNew'; // fallback
    }

    if (filtered.length === 0) {
      return null;
    }
    return (
      <div className="mb-12">
        <h2 className="text-2xl font-bold mb-4 text-accent">{t(sectionKey)}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8">
          {filtered.map((m, i) => (
            <div key={i} className="bg-white/80 dark:bg-black/70 rounded-xl shadow-lg p-4 flex flex-col items-center border border-gray-200 dark:border-gray-700 relative">
              {/* Warranty Badge - Only for New Equipment */}
              {type.toLowerCase() === 'new' && (
                <div className="absolute top-3 right-3 bg-green-500 text-white px-2 py-1 rounded-full text-xs font-bold shadow-md z-10">
                  🛡️ {t('warranty.badge')}
                </div>
              )}
              
              <img src={`/machine-images/${m.ImageURL}`} alt={m.Name} className="h-40 object-contain mb-4 rounded" />
              <div className="font-semibold text-lg mb-1">{m.Name}</div>
              <div className="text-sm text-gray-500 mb-2">{m.Brand} {m.Year} {m.Hours && `| ${m.Hours} hrs`}</div>
              <div className="mb-2">{m.Description}</div>
              
              {/* Warranty Info for New Equipment */}
              {type.toLowerCase() === 'new' && (
                <div className="mb-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg px-3 py-1 w-full">
                  <div className="text-xs text-green-700 dark:text-green-300 font-semibold text-center">
                    ✅ {t('warranty.oneYear')}
                  </div>
                </div>
              )}
              
              <div className="font-bold text-accent text-xl mb-1">{m.Price} <span className="text-base text-gray-600 dark:text-gray-300 font-semibold">{t('pricingPage.priceCurrency')}</span></div>
              <div className="text-sm text-gray-700 dark:text-gray-300 mb-2">{t('pricingPage.shippingEstimate', { estimate: m.ShippingEstimate })}</div>
              <div className="text-xs text-gray-400 mb-2">{m.Status === 'Available' ? t('pricingPage.statusAvailable') : m.Status}</div>
              <button
                className="mt-auto bg-accent text-white px-4 py-2 rounded font-semibold hover:bg-accent/90 transition"
                onClick={() => { setSelectedMachine(m); setIsInquiryModalOpen(true); }}
              >
                {t('pricingPage.requestInfo')}
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  };


  return (
    <div className="min-h-screen bg-gradient-to-b from-primary-light to-secondary-light dark:from-primary-dark dark:to-secondary-dark py-16 px-4 flex flex-col items-center transition-colors duration-300 watermark-bg relative">
      {/* Inquiry Modal for machine-specific requests */}
      <MachineInquiryModal
        machine={selectedMachine}
        open={isInquiryModalOpen}
        onClose={() => { setIsInquiryModalOpen(false); setSelectedMachine(null); }}
      />
      {/* Pricing content */}
      <div className="max-w-6xl w-full mx-auto z-10 pt-24 md:pt-28">
        <h1 className="text-4xl font-extrabold mb-8 text-accent text-center tracking-tight">{t('pricingPage.mainHeading')}</h1>
        
        {/* Learn Savings Button */}
        <div className="text-center mb-12">
          <button
            onClick={handleNavToImports}
            className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-8 py-3 rounded-xl text-lg font-semibold shadow-lg hover:from-green-600 hover:to-emerald-700 transition-all duration-300 transform hover:scale-105"
          >
            {t('pricingPage.learnSavingsButton')}
          </button>
        </div>
        
        {renderMachines('New')}
        {renderMachines('Used')}
        {renderMachines('Sourced')}
      </div>
      <Footer />
    </div>
  );
};

export default PricingPage;
